import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Leaf, DollarSign, Zap, Trees } from "lucide-react";

/**
 * Emotional hero card for the client overview.
 * Pulls /clients/me/impact-summary and shows lifetime $ saved + CO2 avoided.
 */
export default function SavingsHero() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/clients/me/impact-summary").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d || d.active_systems === 0) return null;

  const tiles = [
    { icon: DollarSign, label: "Saved on power", value: `$${Math.round(d.dollars_saved).toLocaleString()}`, hint: "Estimated lifetime savings" },
    { icon: Leaf, label: "CO₂ avoided", value: `${Math.round(d.kg_co2_avoided).toLocaleString()} kg`, hint: "Equivalent emissions kept out of the air" },
    { icon: Zap, label: "Energy produced", value: `${Math.round(d.kwh_produced).toLocaleString()} kWh`, hint: `${d.kw_installed} kW system` },
    { icon: Trees, label: "Like planting", value: `${Math.round(d.trees_equivalent)} trees`, hint: "Annual CO₂ absorption equivalent" },
  ];

  return (
    <div data-testid="savings-hero" className="card-pb p-6 md:p-8 bg-gradient-to-br from-emerald-700 via-emerald-600 to-emerald-700 text-white relative overflow-hidden">
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#C9A356]/15 blur-3xl pointer-events-none"></div>

      <div className="relative">
        <p className="overline text-[#FDE68A]">Your impact</p>
        <h2 className="font-heading text-2xl md:text-3xl mt-2">
          Your solar has powered {Math.round(d.kwh_produced).toLocaleString()} kWh of clean energy.
        </h2>
        <p className="text-sm text-emerald-50/90 mt-1">
          Estimated based on your {d.kw_installed} kW system over {d.years_active} year{d.years_active === 1 ? "" : "s"}.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {tiles.map((t, i) => (
            <div key={i} className="rounded-lg bg-white/10 backdrop-blur-sm border border-white/10 p-4" data-testid={`savings-tile-${i}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-emerald-100/80">
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </div>
              <div className="font-heading text-2xl mt-1.5 tabular-nums">{t.value}</div>
              <div className="text-[10px] text-emerald-100/70 mt-1">{t.hint}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
